'use client'

import { createFileRoute, useRouter, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound, MoreHorizontal, PlusIcon } from 'lucide-react'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import TabLayout, { ActionButton } from '@/shared/layouts/TabLayout'
import { DataTable, type DataTableSorting } from '@workspace/ui/components/DataTable'
import { api, type Role } from '@/shared/lib/api'
import { Button } from '@workspace/ui/components/Button'
import { Menu, MenuItem, MenuPopover, MenuTrigger } from '@workspace/ui/components/Menu'
import { Separator } from '@workspace/ui/components/Separator'
import { confirm } from '@workspace/ui/components/ConfirmDialog'
import { toast } from '@workspace/ui/components/Sonner'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { AuthorizeDialog } from '@/shared/components/user/AuthorizeDialog'
import { useDebounce } from '@/shared/hooks/useDebounce'
import TableDataFooter from '@/shared/components/TableDataFooter'

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
})

export const Route = createFileRoute('/users/roles/')({
    validateSearch: searchSchema,
    component: RouteComponent,
})

const columnHelper = createColumnHelper<Role>()

function RouteComponent() {
    const router = useRouter()
    const navigate = useNavigate({ from: Route.fullPath })
    const searchParams = Route.useSearch()

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const { t } = useTranslation()
    const searchQuery = searchParams.q || ''
    const currentPage = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: searchParams.sortDirection as 'asc' | 'desc' }
        : { sortBy: 'createdAt', sortDirection: 'desc' }

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setCurrentPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
    const [isAuthorizeDialogOpen, setIsAuthorizeDialogOpen] = useState(false)
    const debouncedSearch = useDebounce(searchQuery, 300)

    // Fetch roles with search and pagination
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['roles', currentPage, pageSize, debouncedSearch, sorting],
        queryFn: () =>
            api.role.list({
                page: currentPage,
                take: pageSize,
                q: debouncedSearch ? debouncedSearch.trim() : undefined,
                orderBy: sorting?.sortBy,
                order: sorting?.sortDirection?.toUpperCase() as 'ASC' | 'DESC' | undefined,
            }),
    })

    // Delete role mutation
    const deleteMutation = useMutation({
        mutationFn: async (roleIds: string[]) => {
            // Delete roles sequentially
            for (const id of roleIds) {
                await api.role.delete(id)
            }
            return roleIds
        },
        onSuccess: () => {
            toast.success({
                title: t('common.status.success'),
                description: t('roles.list.deleteSuccess.description'),
            })
            // Refresh the list and clear selection
            refetch()
            setRowSelection({})
        },
        onError: error => {
            errorHandler(error)
        },
        meta: {
            skipGlobalErrorHandler: true,
        },
    })

    const errorHandler = (error: any) => {
        if (error?.status === 403) {
            return toast.error({
                title: 'Bạn không có quyền thực hiện hành động này.',
            })
        }
        if (error?.response?.data?.message?.includes('Cannot delete role that has assigned users')) {
            return toast.error({
                title: 'Không thể xoá vai trò đã được gán cho người dùng.',
            })
        }

        return toast.error({
            title: t('common.status.error'),
            description: t('roles.list.deleteError.description'),
        })
    }

    const links = [
        { to: '/users', label: t('tabs.users') },
        { to: '/users/roles', label: t('tabs.roles') },
    ]

    const handleAuthorize = () => {
        setIsAuthorizeDialogOpen(true)
    }

    const handleActionClick = () => {
        router.navigate({ to: '/users/roles/new' })
    }

    const handleKeydown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (currentPage !== 1) setCurrentPage(1)
            else await refetch()
        }
    }

    const handleBulkDelete = async () => {
        const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id])
        if (selectedIds.length === 0) return
        await deleteMutation.mutateAsync(selectedIds)
    }

    // Define table columns with actions
    const columns = [
        columnHelper.display({
            id: 'actions',
            header: '',
            cell: ({ row }) => (
                <MenuTrigger>
                    <Button variant="outline" size="iconSm" className="rounded-full border-foreground w-4 h-4">
                        <MoreHorizontal className="!size-3" />
                    </Button>
                    <MenuPopover>
                        <div className="px-3 py-2 font-semibold text-sm">{t('roles.list.actions.menu')}</div>
                        <Separator />
                        <Menu className="min-w-[120px]">
                            <MenuItem
                                onAction={() => {
                                    router.navigate({ to: `/users/roles/${row.original.id}/edit` })
                                }}
                                className="cursor-pointer"
                            >
                                {t('common.actions.edit')}
                            </MenuItem>
                            <MenuItem
                                onAction={() => {
                                    router.navigate({ to: `/users/roles/new`, search: { id: row.original.id } })
                                }}
                                className="cursor-pointer"
                            >
                                {t('common.actions.duplicate')}
                            </MenuItem>
                            <MenuItem
                                isDisabled={row.original.isSystemRole}
                                onAction={() => {
                                    confirm({
                                        title: t('roles.list.deleteConfirm.title'),
                                        description: t('roles.list.deleteConfirm.description'),
                                        action: {
                                            label: t('common.actions.delete'),
                                            onClick: async () => {
                                                await deleteMutation.mutateAsync([row.original.id])
                                            },
                                        },
                                        cancel: {
                                            label: t('common.actions.cancel'),
                                        },
                                        variant: 'destructive',
                                    })
                                }}
                                className="cursor-pointer text-destructive"
                            >
                                {t('common.actions.delete')}
                            </MenuItem>
                        </Menu>
                    </MenuPopover>
                </MenuTrigger>
            ),
            size: 80,
            enableSorting: false,
            meta: {
                className: '!max-w-[40px] !min-w-[40px] !w-[40px]',
            },
        }),
        columnHelper.accessor('displayName', {
            header: t('roles.list.table.role'),
            size: 150,
            enableSorting: true,
            cell: ({ row, getValue }) => {
                const value = getValue()
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => {
                            router.navigate({ to: `/users/roles/${row.original.id}/edit` })
                        }}
                    >
                        {value || '-'}
                    </div>
                )
            },
        }),
        columnHelper.accessor('description', {
            header: t('roles.list.table.description'),
            enableSorting: false,
            cell: ({ getValue }) => {
                const value = getValue()
                return value || '-'
            },
        }),
        columnHelper.accessor('userCount', {
            header: t('roles.list.table.accounts'),
            enableSorting: false,
            cell: ({ getValue }) => {
                const value = getValue()
                return value ?? '-'
            },
            size: 140,
        }),
        columnHelper.accessor('createdAt', {
            header: t('roles.list.table.createdDate'),
            enableSorting: true,
            cell: ({ getValue }) => {
                const value = getValue()
                return value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'
            },
            size: 140,
        }),
    ]

    const totalResults = data?.meta.itemCount || 0

    const actions: ActionButton[] = [
        {
            text: t('roles.list.authorizeButton'),
            icon: KeyRound,
            onClick: handleAuthorize,
            variant: 'outline',
            iconClassName: 'size-4!',
            className: 'border-border-medium px-4',
        },
        {
            text: t('roles.list.createButton'),
            icon: PlusIcon,
            onClick: handleActionClick,
            variant: 'default',
        },
    ]

    return (
        <TabLayout
            className="h-[calc(100vh-150px)] min-h-[500px] overflow-hidden"
            title={t('roles.list.title')}
            linksTo={links}
            actions={actions}
        >
            <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                {/* Search Input */}
                <div className="shrink-0">
                    <BsSearchField
                        autoTrim
                        className="h-10 text-sm bg-background rounded-lg w-80 max-lg:w-full"
                        value={searchQuery}
                        placeholder={t('roles.list.searchPlaceholder')}
                        onChange={value => setSearchQuery(value)}
                        onKeyDown={handleKeydown}
                    />
                </div>

                {/* Table */}
                <DataTable
                    columns={columns}
                    data={data?.data || []}
                    enableRowSelection={row => !row.original.isSystemRole}
                    enableSorting={true}
                    sorting={sorting}
                    setSorting={setSorting}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    isLoading={isLoading}
                    emptyString={t('common.emptyState')}
                    containerClassName="flex-1 overflow-y-auto min-h-0"
                />

                <div className="shrink-0">
                    <TableDataFooter
                        selectedCount={Object.keys(rowSelection).length}
                        onDelete={handleBulkDelete}
                        deleteDisabled={Object.keys(rowSelection).length === 0 || deleteMutation.isPending}
                        isLoading={deleteMutation.isPending}
                        totalCount={totalResults}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalPages={Math.ceil(totalResults / pageSize)}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={value => setPageSize(value || 10)}
                    />
                </div>
            </div>
            <AuthorizeDialog open={isAuthorizeDialogOpen} onOpenChange={setIsAuthorizeDialogOpen} />
        </TabLayout>
    )
}
